import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Download, Printer, MessageCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, downloadPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";

const VITAL_LABELS = { bp: "BP", pulse: "Pulse", spo2: "SpO2", temperature: "Temp", weight: "Weight", height: "Height", rbs: "RBS" };

export default function VisitView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [sigUrl, setSigUrl] = useState(null);

    useEffect(() => {
        api.get(`/visits/${id}`).then(({ data }) => setData(data)).catch((e) => { toast.error(fmtErr(e)); navigate("/patients"); });
    }, [id]);

    useEffect(() => {
        if (data?.visit?.include_signature && data?.doctor?.has_signature && data?.doctor?.id) {
            api.get(`/team/${data.doctor.id}/signature`, { responseType: "blob" })
                .then((res) => setSigUrl(window.URL.createObjectURL(res.data)))
                .catch(() => {});
        }
    }, [data]);

    if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E06D1F]" data-testid="visit-loading" /></div>;

    const { visit, patient, doctor } = data;
    const vitals = Object.entries(visit.vitals || {}).filter(([, v]) => v);

    const waText = [
        `Prescription - ${doctor.clinic_name || "Zen Hospital"}`,
        `Doctor: ${doctor.name?.startsWith("Dr") ? doctor.name : `Dr. ${doctor.name}`}`,
        `Patient: ${patient.name} (${patient.patient_uid}) | Date: ${visit.date?.slice(0, 10)}`,
        visit.diagnosis ? `Diagnosis: ${visit.diagnosis}` : "",
        "Medicines:",
        ...(visit.medicines || []).map((m, i) => `${i + 1}. ${m.form} ${m.name} ${m.strength} - ${m.dosage}, ${m.frequency}, ${m.timing}, ${m.duration}`),
        visit.advice ? `Advice: ${visit.advice}` : "",
        (visit.follow_up_text || visit.follow_up) ? `Follow-up: ${visit.follow_up_text || visit.follow_up}` : "",
    ].filter(Boolean).join("\n");

    const download = async () => {
        setDownloading(true);
        try {
            await downloadPdf(`/visits/${id}/pdf`, `Rx_${patient.patient_uid}_V${visit.visit_no}.pdf`);
            toast.success("Prescription PDF downloaded");
        } catch (e) {
            toast.error(fmtErr(e, "Failed to generate PDF"));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="space-y-5 animate-fade-up" data-testid="visit-view-page">
            <div className="flex flex-wrap items-center justify-between gap-3 no-print">
                <button onClick={() => navigate(`/patients/${patient.id}`)} data-testid="back-to-patient-btn" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#E06D1F]">
                    <ArrowLeft className="h-4 w-4" /> {patient.name}
                </button>
                <div className="flex gap-2">
                    <Button data-testid="download-pdf-btn" onClick={download} disabled={downloading} className="bg-[#E06D1F] hover:bg-[#B45309] text-white gap-1.5">
                        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
                    </Button>
                    <Button data-testid="print-rx-btn" variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/visits/${id}/pdf`, "_blank")} className="gap-1.5">
                        <Printer className="h-4 w-4" /> Print Rx
                    </Button>
                    {patient.phone && (
                        <a
                            data-testid="share-rx-whatsapp"
                            href={`https://wa.me/91${patient.phone.replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(waText)}`}
                            target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-3.5 py-2 text-sm font-medium text-green-700 hover:bg-green-100 transition-colors duration-150"
                        >
                            <MessageCircle className="h-4 w-4" /> WhatsApp Rx
                        </a>
                    )}
                </div>
            </div>

            <div className="max-w-3xl mx-auto rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="prescription-preview">
                <div className="bg-[#E06D1F] px-6 py-5 text-white flex flex-wrap justify-between gap-3">
                    <div>
                        <h2 className="font-heading text-xl font-bold">{doctor.clinic_name || "My Clinic"}</h2>
                        <p className="text-xs text-emerald-100 mt-0.5">{doctor.clinic_address}{doctor.clinic_phone ? ` · Ph: ${doctor.clinic_phone}` : ""}</p>
                    </div>
                    <div className="text-right">
                        <p className="font-semibold">{doctor.name?.startsWith("Dr") ? doctor.name : `Dr. ${doctor.name}`}</p>
                        <p className="text-xs text-emerald-100">{[doctor.qualification, doctor.specialization].filter(Boolean).join(", ")}</p>
                        {doctor.registration_no && <p className="text-xs text-emerald-100">Reg. No: {doctor.registration_no}</p>}
                    </div>
                </div>

                <div className="px-6 py-4 bg-[#FFF4EC] border-b border-[#F6C9A4] grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                    <p><span className="text-slate-500">Patient:</span> <b>{patient.name}</b></p>
                    <p><span className="text-slate-500">Age/Gender:</span> {patient.age ? `${patient.age} yrs` : "—"} / {patient.gender || "—"}</p>
                    <p><span className="text-slate-500">ID:</span> <span className="font-mono2">{patient.patient_uid}</span></p>
                    <p><span className="text-slate-500">Date:</span> {visit.date?.slice(0, 10)}</p>
                    <p><span className="text-slate-500">Visit:</span> #{visit.visit_no}</p>
                    <p><span className="text-slate-500">Phone:</span> {patient.phone}</p>
                </div>

                <div className="px-6 py-5 space-y-5">
                    {visit.allergy_alerts?.length > 0 && (
                        <div className="rounded-lg border border-red-300 bg-red-50 p-3.5" data-testid="rx-allergy-alerts">
                            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-700"><AlertTriangle className="h-3.5 w-3.5" /> Allergy Alerts</p>
                            {visit.allergy_alerts.map((a, i) => <p key={i} className="mt-1 text-sm text-red-700">{a}</p>)}
                        </div>
                    )}
                    {vitals.length > 0 && (
                        <div data-testid="rx-vitals">
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Vitals</p>
                            <div className="flex flex-wrap gap-2">
                                {vitals.map(([k, v]) => (
                                    <span key={k} className="rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1 text-xs"><b>{VITAL_LABELS[k] || k}:</b> {v}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {visit.symptoms && (
                        <div data-testid="rx-symptoms">
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">Chief Complaints</p>
                            <p className="text-sm text-slate-800 whitespace-pre-line">{visit.symptoms}</p>
                        </div>
                    )}
                    {visit.diagnosis && (
                        <div data-testid="rx-diagnosis">
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">Diagnosis</p>
                            <p className="text-sm font-semibold text-slate-900 whitespace-pre-line">{visit.diagnosis}</p>
                        </div>
                    )}

                    {visit.medicines?.length > 0 && (
                        <div data-testid="rx-medicines">
                            <p className="font-heading text-3xl font-bold text-[#E06D1F] mb-2">&#8478;</p>
                            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-[#E06D1F] text-white text-xs uppercase tracking-wider text-left">
                                        <th className="px-3 py-2">#</th>
                                        <th className="px-3 py-2">Medicine</th>
                                        <th className="px-3 py-2">Dosage</th>
                                        <th className="px-3 py-2">Frequency</th>
                                        <th className="px-3 py-2">Timing</th>
                                        <th className="px-3 py-2">Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visit.medicines.map((m, i) => (
                                        <tr key={i} className={i % 2 ? "bg-slate-50/70" : "bg-white"}>
                                            <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                                            <td className="px-3 py-2">
                                                <p className="font-semibold text-slate-900">{m.form} {m.name} {m.strength}</p>
                                                {m.generic && <p className="text-xs text-slate-500">{m.generic}</p>}
                                            </td>
                                            <td className="px-3 py-2">{m.dosage || "—"}</td>
                                            <td className="px-3 py-2 font-mono2 text-xs">{m.frequency || "—"}</td>
                                            <td className="px-3 py-2">{m.timing || "—"}</td>
                                            <td className="px-3 py-2">{m.duration || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {visit.advice && (
                        <div data-testid="rx-advice">
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">Advice</p>
                            <p className="text-sm text-slate-800 whitespace-pre-line">{visit.advice}</p>
                        </div>
                    )}
                    {visit.notes && (
                        <div data-testid="rx-notes">
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">Clinical Notes</p>
                            <p className="text-sm text-slate-600 whitespace-pre-line">{visit.notes}</p>
                        </div>
                    )}
                    {visit.follow_up && <p className="text-sm" data-testid="rx-followup"><span className="text-slate-500">Follow-up:</span> <b>{visit.follow_up}</b></p>}

                    <div className="pt-8 text-right">
                        {sigUrl ? (
                            <img src={sigUrl} alt="Doctor signature" className="ml-auto h-14 object-contain mb-1" data-testid="rx-signature-img" />
                        ) : (
                            <p className="text-xs text-slate-400 italic">(Digitally signed)</p>
                        )}
                        <p className="font-heading font-semibold text-slate-900">{doctor.name?.startsWith("Dr") ? doctor.name : `Dr. ${doctor.name}`}</p>
                        <p className="text-xs text-slate-500">{[doctor.qualification, doctor.specialization].filter(Boolean).join(", ")}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
